/**
 * Role-based data visibility hints attached to outbound API requests.
 *
 * Admin UI configures per-role scopes (see DataAccessView): own | team |
 * department | branch_location | all. Non-admin snapshots persist `dataAccess`
 * from login + GET /global-settings/me/permissions.
 *
 * hrms-backend MUST enforce these hints server-side (JWT + DB role row).
 * Frontend merging is UX consistency only — never sole authorization.
 */

import { getAuthItem } from "../utils/authStorage";

export const RBAC_SNAPSHOT_STORAGE_KEY = "rbacSnapshot";

/** Impossible ID marker so legacy backends that filter by CSV IDs return empty sets. */
export const EMPTY_SCOPE_SENTINEL = "-1";

function safeParseSnapshot(raw) {
  try {
    if (!raw || typeof raw !== "string") return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function readRbacSnapshotFromStorage() {
  if (typeof window === "undefined") return null;
  try {
    return safeParseSnapshot(getAuthItem(RBAC_SNAPSHOT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function parseCsvIds(val) {
  if (val === undefined || val === null || val === "") return [];
  return String(val)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
}

export function intersectCsvIds(existingCsv, allowedSet) {
  if (!allowedSet || allowedSet.size === 0) return EMPTY_SCOPE_SENTINEL;
  const requested = parseCsvIds(existingCsv);
  if (!requested.length) {
    const sorted = [...allowedSet].sort((a, b) => a - b);
    return sorted.join(",");
  }
  const merged = requested.filter((id) => allowedSet.has(id));
  return merged.length ? merged.join(",") : EMPTY_SCOPE_SENTINEL;
}

function readLoggedInEmployeeProfileId() {
  if (typeof window === "undefined") return null;
  try {
    const raw = getAuthItem("userId");
    if (raw === undefined || raw === null || raw === "") return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

/**
 * @returns {{
 *   restricted: boolean,
 *   level?: string,
 *   employeeId?: number|null,
 *   departmentIds?: number[],
 *   departmentIdSet?: Set<number>,
 *   branchIds?: number[],
 *   branchIdSet?: Set<number>,
 * }}
 */
export function resolveEffectiveScope(snapshot) {
  if (!snapshot || snapshot.isAdmin) {
    return { restricted: false };
  }
  const da = snapshot.dataAccess;
  if (!da || !da.level || da.level === "all") {
    return { restricted: false };
  }

  const level = String(da.level);
  const selectedItems = Array.isArray(da.selectedItems)
    ? da.selectedItems.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
    : [];

  switch (level) {
    case "own":
      return {
        restricted: true,
        level,
        employeeId: readLoggedInEmployeeProfileId(),
      };
    case "team":
      return { restricted: true, level };
    case "department":
      return {
        restricted: true,
        level,
        departmentIds: selectedItems,
        departmentIdSet: new Set(selectedItems),
      };
    case "branch_location":
      return {
        restricted: true,
        level,
        branchIds: selectedItems,
        branchIdSet: new Set(selectedItems),
      };
    default:
      return { restricted: false };
  }
}

/**
 * Merge scope hints + intersect explicit branch/department filters when safe.
 *
 * @param {Record<string, any>} existingParams - axios query params
 * @param {object|null} snapshot - rbac snapshot (or pass null to read storage)
 */
export function mergeScopeIntoParams(existingParams = {}, snapshot = null) {
  const snap = snapshot ?? readRbacSnapshotFromStorage();
  const scope = resolveEffectiveScope(snap);
  if (!scope.restricted) {
    return { ...existingParams };
  }

  const params = { ...existingParams };

  params.dataScopeLevel = scope.level;
  if (scope.level === "own") {
    if (scope.employeeId != null) {
      params.scopeEmployeeId = scope.employeeId;
    }
    return params;
  }

  if (scope.level === "team") {
    return params;
  }

  if (scope.level === "department") {
    if (!scope.departmentIds?.length) {
      params.dataScopeDepartmentIds = "";
      params.department = EMPTY_SCOPE_SENTINEL;
      return params;
    }
    params.dataScopeDepartmentIds = scope.departmentIds.join(",");
    if ("department" in params && params.department !== undefined && params.department !== "") {
      params.department = intersectCsvIds(params.department, scope.departmentIdSet);
    }
    return params;
  }

  if (scope.level === "branch_location") {
    if (!scope.branchIds?.length) {
      params.dataScopeBranchIds = "";
      params.branch = EMPTY_SCOPE_SENTINEL;
      return params;
    }
    params.dataScopeBranchIds = scope.branchIds.join(",");
    if ("branch" in params && params.branch !== undefined && params.branch !== "") {
      params.branch = intersectCsvIds(params.branch, scope.branchIdSet);
    }
    return params;
  }

  return params;
}

function normalizeAxiosPath(config) {
  let url = config.url || "";
  if (!url) return "";
  try {
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      return u.pathname || "";
    }
  } catch {
    /* ignore */
  }
  return String(url).split("?")[0] || "";
}

/**
 * Routes where scoped visibility applies (aligned with Employee / Attendance /
 * payroll lists). Narrow intentionally — expand as backend gains parity.
 */
export function shouldAttachDataScope(pathname, method) {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const m = String(method || "GET").toUpperCase();

  if (m === "GET" && path === "/employees") return true;
  if (m === "GET" && /^\/employees\/(?:counts|relieved)$/.test(path)) return true;
  if (m === "GET" && path.startsWith("/employees/export")) return true;
  if (m === "GET" && path.startsWith("/employees/reports/")) return true;
  if (m === "GET" && /^\/employees\/\d+$/.test(path)) return true;

  if (m === "GET" && path === "/attendance/chunked") return true;
  if (m === "GET" && path === "/attendance/history") return true;
  if (m === "POST" && path === "/attendance/history/query") return true;

  if (m === "GET" && path === "/payroll/investment-headers") return true;
  if (m === "GET" && /^\/payroll\/investment-data\/\d+/.test(path)) return true;

  if (m === "GET" && path === "/leave-adjustments") return true;
  if (m === "GET" && path === "/leave-balance") return true;
  if (m === "GET" && path === "/attendance-structure/leave-adjustments") return true;
  if (m === "GET" && path === "/attendance-structure/leave-balance") return true;

  return false;
}

/**
 * Axios request interceptor helper — mutates config.params for scoped routes.
 *
 * Use config.skipDataScope = true to bypass (rare tooling / super-admin flows).
 */
export function attachDataScopeToAxiosConfig(config) {
  if (!config || config.skipDataScope) return config;
  const pathname = normalizeAxiosPath(config);
  const method = config.method || "get";
  if (!shouldAttachDataScope(pathname, method)) return config;

  const snapshot = readRbacSnapshotFromStorage();
  config.params = mergeScopeIntoParams(config.params || {}, snapshot);
  return config;
}
